import { mapValue, stringValue } from "@/utils/coerce";

/**
 * What to say when `llm-trace` will not produce a trace.
 *
 * Extracted from Negative Reviews so Conversations reads the same wording.
 * Conversations is becoming the place traces are looked at (hub §4.1); it
 * should not re-derive these explanations, and the old page should not lose
 * them when it goes.
 */

/**
 * Explain an empty trace.
 *
 * The backend only sets `llm_turn.assistant_message_id` when a turn finishes
 * with `finish_reason == "stop"`, so an errored or cancelled answer leaves no
 * turn linked to its message: the trace comes back 200 with an empty main flow
 * and, because tool calls hang off those turns, no sub-agent flows either.
 *
 * `llm-trace` exposes neither `llm_turn.error` nor the message metadata, so it
 * cannot explain itself. The caller has to hand over the message's metadata —
 * the QA detail response carries it as `target_message.metadata`, and
 * `/admin/sessions/{id}/messages` carries it as `metadata`.
 */
export function emptyTraceNotice(message: unknown) {
  const metadata = mapValue(mapValue(message).metadata);
  const finishReason = stringValue(metadata.finish_reason, "");
  const error = mapValue(metadata.error);
  const errorMessage = stringValue(error.message, "");
  const cancelled = metadata.cancelled === true;

  if (!finishReason && !errorMessage && !cancelled) {
    // Nothing to go on — do not guess at a cause.
    return <p>이 답변에 연결된 실행 기록이 없습니다.</p>;
  }

  const reason = cancelled ? "cancelled" : finishReason || "비정상 종료";
  return (
    <div className="grid gap-1">
      <p>
        답변이 <code>{reason}</code>로 끝나 turn이 메시지에 연결되지 않았습니다 — llm_turn
        기록 자체는 남아 있지만 trace로는 조회되지 않습니다.
      </p>
      {errorMessage && (
        <p className="text-muted-foreground">{errorMessage.slice(0, 300)}</p>
      )}
    </div>
  );
}

/**
 * Distinguish the documented failures instead of dumping the raw message.
 *
 * The four stay four. Each one implies a different next move — look for the
 * message elsewhere, pick a different row, fix the deployment's config, retry
 * — and collapsing them into "trace unavailable" throws away the only part of
 * the response that tells the reader which.
 */
export function traceErrorNotice(traceError: string) {
  if (traceError.startsWith("MESSAGE_NOT_FOUND")) {
    return "이 세션에서 해당 메시지를 찾을 수 없습니다 — 하드 삭제되었거나 다른 환경의 메시지입니다.";
  }
  if (traceError.startsWith("MESSAGE_NOT_ASSISTANT")) {
    return "assistant 메시지가 아니어서 trace를 조회할 수 없습니다.";
  }
  // The two prod-pool failures. A prod row is traced like a dev one, so these
  // arrive as a response rather than being predicted from the environment.
  if (traceError.startsWith("PROD_DB_NOT_CONFIGURED")) {
    return "prod DB가 설정되지 않아 trace를 조회할 수 없습니다.";
  }
  if (traceError.startsWith("PROD_TRACE_LOAD_FAILED")) {
    return "prod DB에서 trace를 읽지 못했습니다.";
  }
  return `LLM trace를 불러오지 못했습니다 — ${traceError}`;
}
